/**
 * Backsheets API.
 */

import { apiFetch, apiFormFetch } from './fetch';
import { API } from '../constants';
import type { Backsheet, BacksheetExpandRow } from '../types/dvms.types';

export async function listBacksheets(): Promise<Backsheet[]> {
    return apiFetch<Backsheet[]>(API.BACKSHEETS);
}

export async function createBacksheet(payload: Backsheet): Promise<Backsheet> {
    return apiFetch<Backsheet>(API.BACKSHEETS, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function importBacksheets(payload: Backsheet[]): Promise<Backsheet[]> {
    return apiFetch<Backsheet[]>(`${API.BACKSHEETS}/import`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function addBacksheetExpandRow(backsheetId: string, row: BacksheetExpandRow): Promise<BacksheetExpandRow> {
    return apiFetch<BacksheetExpandRow>(`${API.BACKSHEETS}/${backsheetId}/rows`, {
        method: 'POST',
        body: JSON.stringify(row),
    });
}

export async function removeBacksheetExpandRow(backsheetId: string, rowId: string): Promise<void> {
    await apiFetch(`${API.BACKSHEETS}/${backsheetId}/rows/${rowId}`, { method: 'DELETE' });
}

export async function updateBacksheet(id: string, payload: Partial<Backsheet>): Promise<Backsheet> {
    return apiFetch<Backsheet>(`${API.BACKSHEETS}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

export async function deleteBacksheet(id: string): Promise<void> {
    await apiFetch(`${API.BACKSHEETS}/${id}`, { method: 'DELETE' });
}

export async function approveAllBacksheets(projectId: string): Promise<void> {
    await apiFetch(`${API.BACKSHEETS}/approve-all`, {
        method: 'POST',
        body: JSON.stringify({ projectId }),
    });
}

// ─── Backsheet Export (DUBVM-102) ───────────────────────────────────────────

export interface BacksheetExportResponse {
    /** Presigned GET URL that downloads the workbook as fileName. */
    url: string;
    /** S3 key: {projectId}/export/Backsheet_<title>_<ms>.xlsx */
    key: string;
    fileName: string;
}

/**
 * Ask the API to build the project's Backsheet and store it in S3. Rejects when
 * storage is down (502/504) — callers then build the workbook in the browser.
 */
export async function exportBacksheet(projectId: string): Promise<BacksheetExportResponse> {
    return apiFetch<BacksheetExportResponse>(`${API.PROJECTS}/${encodeURIComponent(projectId)}/backsheet-export`, {
        method: 'POST',
    });
}

// ─── Backsheet Bulk Import ──────────────────────────────────────────────────

export interface BacksheetImportLineItem {
    /** Optional; when set the API pulls the matching Rate Card (DUBVM-113). */
    subServiceName?: string;
    vendorName: string;
    currencyCode: string;
    currencyConversionRate: number;
    unitType: string;
    quantity: number;
    costLocalCurrency: number;
    costInvoiceCurrency: number;
    totalCost: number;
    rateCard: number;
    markup: number;
    rateInvoiceCurrency: number;
    totalInvoiceAmount: number;
    status: string;
}

export interface BacksheetImportServiceGroup {
    serviceTypeName: string;
    languageName: string;
    type: string;
    lineItems: BacksheetImportLineItem[];
}

export interface BacksheetImportRequest {
    serviceGroups: BacksheetImportServiceGroup[];
    baseCurrency?: string;
    rates?: { code: string; rate: number }[];
}

export interface BacksheetImportResult {
    total: number;
    created: number;
    updated: number;
    failed: number;
    sourceFileURL?: string;
    summaryFileURL?: string;
    /** Rows imported without a matching rate card (DUBVM-113). */
    warnings?: BacksheetImportWarning[];
}

export interface BacksheetImportWarning {
    row: number;
    message: string;
}

/**
 * Backsheet bulk import via multipart/form-data.
 * - `file`  — raw CSV (original Excel data for S3 audit trail)
 * - `data`  — JSON with pre-calculated service groups + line items (for persistence)
 */
export async function importBacksheetBulk(
    projectId: string,
    csvFile: File,
    data: BacksheetImportRequest,
): Promise<BacksheetImportResult> {
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('data', JSON.stringify(data));
    return apiFormFetch<BacksheetImportResult>(
        `${API.PROJECTS}/${projectId}/backsheet-import`,
        formData,
        120_000, // 2 min timeout for bulk import
    );
}

// ─── Backsheet Recalculate ──────────────────────────────────────────────────

export interface RecalculateRequest {
    baseCurrency: string;
    rates: { code: string; rate: number }[];
}

/** Recalculate all line items and service group totals with current currency rates. */
export async function recalculateBacksheets(projectId: string, payload: RecalculateRequest): Promise<unknown[]> {
    return apiFetch<unknown[]>(
        `${API.PROJECTS}/${projectId}/backsheet-recalculate`,
        { method: 'POST', body: JSON.stringify(payload) },
        60_000,
    );
}
